import { defineStore, type _GettersTree } from 'pinia'
import type { IModalConfig, IModalProps, IModalPropsInternal } from 'components/modal/types'
import { v4 } from 'uuid'
import { getTimeSeconds } from '@/utils/time'
import { useInterpolation } from '@/utils/animation/useInterpolation'
import type { FunctionalComponent, VNode } from 'vue'
import type { SetState } from '@/types/state/setState'
import type { TAnyObject } from '@/types/object'
import { isFunction } from '@/utils/is'
import { average } from 'math'
import { EModalEvent, globalModals, type ModalPatchFun } from '../hooks/useModals'

type ModalMap = Record<string, IModalPropsInternal>

type ModalCloseCallbackFunc = () => any;

export interface IModalStoreState {
  modals: ModalMap
  stack: string[];
  closeCallbacks: Record<string, Array<ModalCloseCallbackFunc>>;
}

export interface IModalStoreActions<T extends TAnyObject = TAnyObject> {
  init: () => Promise<any>;
  pushModal: <K extends TAnyObject = T>(modal: IModalProps<K>) => Promise<void>;
  popModal: (uid?: string) => Promise<void>;
  onModalClosed: (fun: ModalCloseCallbackFunc, uid?: string) => any;
  updateModal: SetState<IModalPropsInternal>;
  clearModals: () => Promise<void>;
}

export interface IModalStoreGetters extends _GettersTree<any> {
  count: () => number,
  animation: () => number
}

export const useModalStore = defineStore<
  'modal',
  IModalStoreState,
  IModalStoreGetters,
  IModalStoreActions<any>
>('modal', {
  state() {
    return {
      modals: {} as ModalMap,
      stack: [] as string[],
      closeCallbacks: {}
    }
  },
  getters: {
    count() {
      return Object.values(this.modals).length
    },
    animation() {
      const modals = Object.values(this.modals).filter(it => !!it);
      const numbers = modals.map(it => it.animation.state.value ?? 0);
      return average(numbers);
    }
  },
  actions: {
    async init() {
      /* noop */
      return true
    },
    async clearModals() {
      const uids = this.stack || [];

      for (const uid of uids) {
        await this.popModal(uid);
      }
    },
    updateModal(fun) {
      this.$patch((s) => {
        const stack = s.stack
        if (!stack || stack.length <= 0) return
        const uid = stack[stack.length - 1]
        const current = s.modals[uid]
        if (!current) return
        s.modals[uid] = fun(current)
      })
    },
    async pushModal(modal: IModalProps<any>) {
      const uid = v4()

      if (this.$state.modals[uid]) return

      const started = getTimeSeconds()
      const animation = useInterpolation({
        duration: 0.5,
        initial: 0.0
      })

      const handleMount = async (node: VNode) => {
        this.$patch((s) => {
          ;(s.modals[uid] || ({} as IModalPropsInternal)).node = node
        })

        await animation.run(0.0, 1.0)
      }

      const nextModal = {
        ...modal,
        uid,
        animation: {
          started: started,
          state: animation.state,
          handle: animation
        },
        state: {
          trash: false
        },
        onVnodeMounted: handleMount
      } as IModalPropsInternal

      this.$patch((s: IModalStoreState): void => {
        s.modals[uid] = nextModal
        s.stack.push(uid)
      })
    },
    async popModal(uid?: string) {
      if (this.stack.length <= 0) return
      uid = uid || this.stack[this.stack.length - 1]
      if (!uid) return
      const modal = this.$state.modals[uid]
      if (!modal) return;
      if (this.$state.modals[uid].state.trash) return;

      this.$state.modals[uid].state.trash = true;

      await modal.animation.handle.run(1.0, 0.0)

      const callbacks = this.$state.closeCallbacks[uid];

      if (modal.onClose) {
        modal.onClose()
      }

      if (callbacks && callbacks.length > 0) {
        callbacks.forEach(fn => fn());
      }

      this.$patch((s) => {
        if (!uid) return

        s.stack = s.stack.filter((u) => u !== uid)
        s.modals[uid].animation.handle.cancel();
        s.closeCallbacks[uid] = [];
        delete s.modals[uid]
      })
    },
    onModalClosed(fun: ModalCloseCallbackFunc, uid?: string) {
      if (this.stack.length <= 0) return
      uid = uid || this.stack[this.stack.length - 1]
      if (!uid) return
      const modal = this.$state.modals[uid]
      if (!modal) return;

      this.$patch((s) => {
        if (!uid) return

        const arr = s.closeCallbacks[uid];
        if (!arr) {
          s.closeCallbacks[uid] = [fun];
          return;
        }
        s.closeCallbacks[uid] = [...s.closeCallbacks[uid], fun];
      })
    }
  }
})

export const pushModal = async (props: IModalConfig | FunctionalComponent<any>) => {
//  const modalStore = useModalStore();

  if (isFunction(props)) {
    return await globalModals.push({
      body: props
    })
  }

  return await globalModals.push(props);
}

export const popModal = async (uid?: string) => {
  return await globalModals.pop(uid);
}

export const clearModals = async () => {
  return await globalModals.clear();
}

export const patchModal = (fn: ModalPatchFun, id?: string) => {
  return globalModals.patch(fn, id);
}

export const onModalClosed = (fn: ModalCloseCallbackFunc, uid?: string) => {
  globalModals.events.subscribe(EModalEvent.CLOSE, ({ id }) => {
    if (uid && id !== uid) return;
    fn();
  })
  //const modalStore = useModalStore();
  //modalStore.onModalClosed(fn, uid);
}

// @ts-ignore
window.pushTestModal = () => pushModal({ title: 'test' })
